import { useState, useEffect, useMemo } from 'react';
import { Search, Instagram, Facebook } from 'lucide-react';
import { Link } from 'react-router-dom';
import { products } from '@/data/products';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import xtremeTackImage from '@/assets/xtreme-tack-white-grip.webp';
interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const socialLinks = [{
  icon: Instagram,
  href: 'https://instagram.com/grizzlee_gripz_pb/',
  label: 'Instagram'
}, {
  icon: () => <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
      </svg>,
  href: 'https://tiktok.com/@grizzlee.gripz',
  label: 'TikTok'
}, {
  icon: Facebook,
  href: 'https://facebook.com/GRIZZLEEGRIPZ',
  label: 'Facebook'
}];

// Featured categories with correct routes
const featuredCategories = [
  { id: 'xtreme', name: 'Xtreme Tacky Grips', image: '/lovable-uploads/5ef37510-2fdd-4779-962e-a1bea2b7b800.png', route: '/grips/xtreme-tacky', price: 14.99 },
  { id: 'comfort', name: 'Comfort Grips', image: '/lovable-uploads/b8570872-e5a9-41a5-af55-67ad545464a7.webp', route: '/grips/comfort', price: 12.99 },
  { id: 'apparel', name: 'Shop Apparel', image: '/lovable-uploads/5ef37510-2fdd-4779-962e-a1bea2b7b800.png', route: '/apparel', price: 29.99 },
  { id: 'accessories', name: 'Accessories', image: '/lovable-uploads/b8570872-e5a9-41a5-af55-67ad545464a7.webp', route: '/accessories', price: 19.99 },
];
const SearchModal = ({
  isOpen,
  onClose
}: SearchModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return products.filter(p => p.name.toLowerCase().includes(query) || p.description.toLowerCase().includes(query) || p.category.toLowerCase().includes(query)).slice(0, 6);
  }, [searchQuery]);
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);
  return <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="sr-only">
          <SheetTitle>Search Products</SheetTitle>
        </SheetHeader>
        
        {/* Search Input */}
        <div className="relative border-b border-border pt-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground mt-2" />
          <input type="text" placeholder="Search products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-4 text-lg bg-transparent focus:outline-none" autoFocus />
        </div>

        {/* Content */}
        <div className="p-4 flex-1 overflow-y-auto">
          {/* Featured Product Banner */}
          {!searchQuery.trim() && (
            <Link to="/grips/xtreme-tacky" onClick={onClose} className="block mb-6 group">
              <div className="relative rounded-xl overflow-hidden">
                <img 
                  src={xtremeTackImage} 
                  alt="Xtreme Tack White Grip" 
                  className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 text-white">
                  <p className="text-xs font-medium text-primary">FEATURED</p>
                  <p className="font-bold">Xtreme Tack Grips</p>
                </div>
              </div>
            </Link>
          )}

          {/* Categories Section - shown when not searching */}
          {!searchQuery.trim() && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-muted-foreground mb-3">Shop Categories</h3>
              <div className="grid grid-cols-2 gap-3">
                {featuredCategories.map(category => (
                  <Link key={category.id} to={category.route} onClick={onClose} className="group flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors">
                    <div className="w-14 h-14 rounded-md overflow-hidden bg-muted flex-shrink-0">
                      <img src={category.image} alt={category.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                        {category.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        From ${category.price.toFixed(2)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Search Results Section */}
          {searchQuery.trim() && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-muted-foreground mb-3">Search Results</h3>
              {filteredProducts.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {filteredProducts.map(product => (
                    <Link key={product.id} to={`/${product.category.toLowerCase()}`} onClick={onClose} className="group flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors">
                      <div className="w-14 h-14 rounded-md overflow-hidden bg-muted flex-shrink-0">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                          {product.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          ${product.price.toFixed(2)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No products found for "{searchQuery}"
                </p>
              )}
            </div>
          )}

          {/* Quick Links */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">Quick Links</h3>
            <div className="flex flex-wrap gap-2">
              {['Grips', 'Apparel', 'Accessories', 'Best Sellers'].map(link => <Link key={link} to={link === 'Best Sellers' ? '/grips?filter=bestseller' : `/${link.toLowerCase()}`} onClick={onClose} className="px-3 py-1.5 text-sm bg-muted rounded-full transition-colors hover:bg-primary text-primary-foreground">
                  {link}
                </Link>)}
            </div>
          </div>

          {/* Social Media Section */}
          <div className="pt-4 border-t border-border">
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">Follow Us</h3>
            <div className="flex items-center gap-3">
              {socialLinks.map((social, index) => <a key={index} href={social.href} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full transition-all duration-300 text-black hover:-translate-y-1 hover:scale-110 hover:bg-gradient-to-br hover:from-orange-400 hover:to-orange-600 hover:text-gray-200 hover:shadow-[0_0_20px_rgba(251,146,60,0.6)] hover:rotate-6 hover:[&_svg]:drop-shadow-[0_0_3px_rgba(255,255,255,0.5)]" aria-label={social.label}>
                  <social.icon />
                </a>)}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>;
};
export default SearchModal;